import type { ActionTree, MutationTree } from "vuex";
import { moveIdx } from "@user-center/utils";
import lodash, { cloneDeep } from "lodash-es";
export interface LayoutElementBaseConfig {
  //页面元素基础配置
  backgroundColor: string;
  backgroundImage: string;
  showTime: string[];
  height?: number;
  padding: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  margin: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

export type LinkType = {
  // 页面元素基础配置
  type: "good" | "active" | "page" | "appPage" | "link";
  value: string | number;
};
export type SelectGood = {
  type: 1 | 2;
  id: string | number;
};
export interface LayoutElementGoods extends LayoutElementBaseConfig {
  // 商品专区类型
  showStyle: "row" | "col"; // 展示馆方式单列，多列
  columnNum: number; // 每行格数，单行的话无效
  gutter: number; // 多列间隔距离
  url: string; // 请求的地址

  selectGoods: SelectGood[]; // 选择的商品
}

export interface LayoutElementImageGrid extends LayoutElementBaseConfig {
  // 广告图专区类型
  imageConfigs: Array<{
    url: string; // 对应图片地址
    link?: LinkType; // 对应图片跳转链接
  }>; // 对应图片配置
  gutter: number; // 多列间隔距离
}
export interface LayoutElementBanner extends LayoutElementBaseConfig {
  // 轮播图专区类型
  imageConfigs: Array<{
    url: string; // 对应图片地址
    link?: LinkType; // 对应图片跳转链接
  }>; // 对应图片配置
}

// 栅格分类
export interface LayoutElementGridCategory extends LayoutElementBaseConfig {
  columnNum: number; //每行格数
  isSwipePage: boolean; //是否多个分类按分页展示
  gridBackgroundColor: string;
  border: boolean; //是否有边框
  imageWidth?: number;
  imageHeight?: number;
  maxPageNum?: number; //
  categoryConfigs: Array<{
    title?: string; //对应分类标题
    url?: string; //对应分类图片地址
    link?: LinkType; //对应分类跳转链接
  }>; //对应分类配置
}
// 列表分类
export interface LayoutElementListCategory extends LayoutElementBaseConfig {
  showStyle: "line" | "card"; //展示类型按下划线展示，按块级展示
  activeColor: string;
  imageWidth?: number;
  imageHeight?: number;
  categoryConfigs: Array<{
    title?: string; //对应分类标题
    url?: string; //对应分类图片地址
    link?: string | number; //对应分类跳转链接
  }>; //对应分类配置
}

export interface LayoutElementGoodsList extends LayoutElementBaseConfig {
  // 商品列表类型
  showStyle: "row" | "col"; // 展示馆方式单列，多列
  columnNum: number; // 每行格数，单行的话无效
  gutter: number; // 多列间隔距离
  url: string; // 请求的地址
  isHaveCategory: boolean; //是否显示对应商品分类
  isHaveFilter: boolean; //是否显示对应过滤标准

  categoryShowStyle: "line" | "card"; //展示类型按下划线展示，按块级展示
  activeColor?: string;
  categoryImageWidth?: number;
  categoryImageHeight?: number;
  selectGoods: SelectGood[]; // 选择的商品
  categoryConfigs?: Array<{
    title?: string; //对应分类标题
    url?: string; //对应分类图片地址
    value?: string; //对应分类值
  }>; //对应分类配置
}
export interface LayoutElementText extends LayoutElementBaseConfig {
  //  文字类型
  content: string; // 文字内容
  fontSize: number; // 字体大小
  lineHeight: number; // 文字行高
  color: string; //颜色类型
  textAlign: "left" | "right" | "center"; // 文字对齐方式
}
export type LayoutElementConfig =
  | LayoutElementGoods
  | LayoutElementImageGrid
  | LayoutElementBanner
  | LayoutElementListCategory
  | LayoutElementGridCategory
  | LayoutElementGoodsList
  | LayoutElementText;
export interface LayoutElement {
  type: string; // "goods" | "banner" | "imageGrid" | "gridCategory" |"listCategory" | "goodsList" | "text";
  config: LayoutElementConfig;
}

export interface PageEntityInfo {
  title: string; // 页面标题
  backgroundColor: string; // 页面背景色
  backgroundImage: string; // 页面图片
}

export interface PageEntity {
  info: PageEntityInfo;
  elements: LayoutElement[];
}
export interface AppEditorStateType {
  detailData: any; //保存服务端响应数据
  pages: PageEntity[]; //页面列表
  currentPageIndex: number; //选中页
  currentElementIndex: number; //选中元素
}
export const baseConfigData: LayoutElementBaseConfig = {
  // 默认的基础配置值
  showTime: [],
  backgroundColor: "",
  backgroundImage: "",
  padding: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  margin: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
};
export const defaultGoodsElementData: LayoutElement = {
  // 默认的商品专区配置值
  type: "goods",
  config: {
    showStyle: "col",
    columnNum: 2,
    gutter: 5,
    url: "",
    selectGoods: [],
    ...lodash.cloneDeep(baseConfigData),
  },
};
export const defaultGoodsListElementData: LayoutElement = {
  // 默认的商品列表配置值
  type: "goodsList",
  config: {
    showStyle: "col",
    columnNum: 2,
    gutter: 5,
    url: "",
    selectGoods: [],
    isHaveCategory: false,
    isHaveFilter: true,
    categoryShowStyle: "line",
    categoryConfigs: [],
    ...lodash.cloneDeep(baseConfigData),
  },
};
export const defaultImageGridElementData: LayoutElement = {
  // 默认的商品专区配置值
  type: "imageGrid",
  config: {
    gutter: 5,
    imageConfigs: [],
    ...lodash.cloneDeep(baseConfigData),
  },
};
export const defaultBannerElementData: LayoutElement = {
  // 默认的商品专区配置值
  type: "banner",
  config: {
    imageConfigs: [],
    ...lodash.cloneDeep(baseConfigData),
    height: 150,
  },
};

export const defaultGridCategoryElementData: LayoutElement = {
  // 默认的栅格分类配置值
  type: "gridCategory",
  config: {
    columnNum: 4,
    isSwipePage: false,
    gridBackgroundColor: "#fff",
    border: false,
    categoryConfigs: [{ title: "默认分类", url: "" }],
    maxPageNum: 8,
    height: 150,
    ...lodash.cloneDeep(baseConfigData),
  },
};
export const defaultListCategoryElementData: LayoutElement = {
  // 默认的列表分类配置值
  type: "listCategory",
  config: {
    showStyle: "line",
    activeColor: "var(--el-color-primary)",
    categoryConfigs: [{ title: "默认分类", url: "" }],
    ...lodash.cloneDeep(baseConfigData),
  },
};
export const defaultPageData: PageEntity = {
  info: {
    title: "默认页面",
    backgroundColor: "#fff",
    backgroundImage: "",
  },
  elements: [],
};
export const state: AppEditorStateType = {
  detailData: {},
  pages: [lodash.cloneDeep(defaultPageData)],
  currentPageIndex: 0,
  currentElementIndex: 0,
};
export interface ChangSettingPayInterface {
  key: string;
  value: any;
}
const mutations: MutationTree<AppEditorStateType> = {
  SET_DETAIL_DATA: (state: AppEditorStateType, data: any) => {
    state.detailData = data || {};
  },
  INIT_PAGE_DATA: (state: AppEditorStateType, data: PageEntity[]) => {
    state.pages = data;
    state.currentPageIndex = 0;
    state.currentElementIndex = 0;
  },
  ADD_ELEMENT: (state: AppEditorStateType, data: LayoutElement) => {
    state.pages[state.currentPageIndex].elements.push(data);
    state.currentElementIndex =
      state.pages[state.currentPageIndex].elements.length - 1;
  },
  ADD_PAGE: (state: AppEditorStateType, data: PageEntity) => {
    state.pages.push(data);
  },
  SELECT_PAGE: (state: AppEditorStateType, pageIdx: number) => {
    state.currentPageIndex = pageIdx;
    state.currentElementIndex = 0;
  },
  REMOVE_PAGE: (state: AppEditorStateType, pageIdx: number) => {
    state.pages.splice(pageIdx, 1);
    state.currentPageIndex = 0;
    state.currentElementIndex = 0;
  },
  REMOVE_ELEMENT: (state: AppEditorStateType, removeElementIdx: number) => {
    state.pages[state.currentPageIndex].elements.splice(removeElementIdx, 1);
  },
  SELECT_ELEMENT: (state: AppEditorStateType, elementIdx: number) => {
    state.currentElementIndex = elementIdx;
  },
  MOVE_ELEMENT_UP: (state: AppEditorStateType, elementIdx: number) => {
    moveIdx(state.pages[state.currentPageIndex].elements, -1, elementIdx);
    state.currentElementIndex = elementIdx - 1;
  },
  MOVE_ELEMENT_DOWN: (state: AppEditorStateType, elementIdx: number) => {
    moveIdx(state.pages[state.currentPageIndex].elements, 1, elementIdx);
    state.currentElementIndex = elementIdx + 1;
  },
  COPY_ELEMENT: (state: AppEditorStateType, elementIdx: number) => {
    state.pages[state.currentPageIndex].elements.splice(
      elementIdx,
      0,
      cloneDeep(state.pages[state.currentPageIndex].elements[elementIdx])
    );
  },
};

const actions: ActionTree<AppEditorStateType, any> = {
  addElement({ commit }, data) {},
  addPage({ commit, state }) {
    const setPageData: PageEntity = lodash.cloneDeep(defaultPageData);
    setPageData.info.title = "默认页面" + (state.pages.length + 1);
    commit("ADD_PAGE", setPageData);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
